import { useMemo } from "react";
import {
  filter,
  find,
  flatMap,
  flatten,
  map,
  orderBy,
  some,
  sumBy,
  times,
} from "lodash";
import { percent } from "@tootools/number";
import { natural } from "@tootools/sort";
import { Gauge } from "../model";

const isIdEqual = (a: string | number, b: string | number) =>
  String(a).toLowerCase() === String(b).toLowerCase();

export const filterGaugesBySortUnit = (gauges: Gauge[], sortUnit: string) =>
  filter(gauges, (gauge) =>
    some(gauge.series, (serie) => isIdEqual(serie.id, sortUnit))
  );

const getOrderedGaugesWithPercentageBySortUnit = (
  gauges: Gauge[],
  sortUnit: string
) => {
  const withPercentGauges = map(gauges, (gauge) => {
    const total = sumBy(gauge.series, "count");
    return {
      id: gauge.id,
      percentage: percent(
        find(gauge.series, (serie) => isIdEqual(serie.id, sortUnit)).count,
        total
      ),
    };
  });

  return orderBy(withPercentGauges, "percentage", "desc");
};

const getNonIncludedGauges = (
  gauges: Gauge[],
  includedGauges: {
    id: string | number;
    percentage: number;
  }[]
) => filter(gauges, (gauge) => !some(includedGauges, { id: gauge.id }));
export function sortGaugesBySubUnit(
  gauges: Gauge[],
  sortUnit: string
): Gauge[] {
  const filteredGaugesBySortUnit = filterGaugesBySortUnit(gauges, sortUnit);
  const orderedGauges = getOrderedGaugesWithPercentageBySortUnit(
    filteredGaugesBySortUnit,
    sortUnit
  );
  const gaugesWithoutSubUnit = getNonIncludedGauges(gauges, orderedGauges);
  const sortedGaugesBySubUnit = map(orderedGauges, (orderedGauge) =>
    find(gauges, (gauge) => gauge.id === orderedGauge.id)
  );

  return flatten([sortedGaugesBySubUnit, gaugesWithoutSubUnit]);
}

export default function useGauges(
  gauges: Gauge[],
  displayNullValues: boolean,
  sort: "alpha" | "default" | string,
  limit: number,
  total: number,
  isFullscreen: boolean,
  chartValuesFormat: "relative" | "absolute"
) {
  const filteredGauges = useMemo(() => {
    if (displayNullValues) {
      return gauges;
    } else {
      const preFilteredGauges = gauges.map((gauge) => ({
        ...gauge,
        series: gauge.series.filter((serie) => serie.count > 0),
      }));
      return preFilteredGauges.filter((gauge) => gauge.series.length > 0);
    }
  }, [displayNullValues, gauges]);

  const getUpperMultiple = (length: number, limit: number) =>
    limit * Math.ceil(length / limit);

  const sortGaugesByDefault = (gauges) =>
    orderBy(gauges, (gauge) => sumBy(gauge.series, "count"), "desc");
  const preSortedGauges =
    sort === "alpha"
      ? natural(filteredGauges, "label")
      : sort === "default"
      ? sortGaugesByDefault(filteredGauges)
      : sort
      ? sortGaugesBySubUnit(filteredGauges, sort)
      : filteredGauges;
  const sortedGauges = useMemo(() => {
    const upperMultiple = getUpperMultiple(preSortedGauges.length, limit);
    const missingRows = upperMultiple - preSortedGauges.length;
    if (missingRows === 0 || isFullscreen) {
      return preSortedGauges;
    } else {
      return [
        ...preSortedGauges,
        ...times(missingRows, () => {
          return { ...preSortedGauges[0], isPhantom: true };
        }),
      ];
    }
  }, [limit, isFullscreen, preSortedGauges]);

  const fullTotal = useMemo(() => {
    if (total) {
      return total;
    } else {
      const allSeries = flatMap(filteredGauges, "series");
      return sumBy(allSeries, "count");
    }
  }, [filteredGauges, total]);

  const getSortedGaugesWithSortCount = () => {
    return map(sortedGauges, (gauge) => ({
      ...gauge,
      sortCount: sumBy(gauge.series, "count"),
    }));
  };

  const getGaugesOrderedByChartValuesFormat = () => {
    const gauges = getSortedGaugesWithSortCount();
    if (chartValuesFormat === "relative") {
      return gauges;
    }

    return orderBy(
      map(gauges, (gauge) => ({
        ...gauge,
        customSortCount: sumBy(
          filter(gauge.series, (serie) => serie.id === sort),
          "count"
        ),
      })),
      ["isPhantom", "customSortCount"],
      ["desc", "desc"]
    );
  };

  return {
    filteredGauges,
    sortedGauges: getGaugesOrderedByChartValuesFormat(),
    fullTotal,
  };
}
